import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from "@angular/router";
import { catchError, of, switchMap } from "rxjs";
import { inject } from "@angular/core";

import { AppLoaderService } from "../../services/app-loader/app-loader.service";
import { AppSessionService } from "../../services/app-session/app-session.service";
import { SettingsService } from "../../services/settings/settings.service";
import { AuthService } from "../../services/auth/auth.service";
import { ApiError } from "../../models/api-error";
import { SystemUsersService } from "../../services/system-users/system-users.service";
import { Session } from "../../models/session";

/**
 * Validate if a user is authenticated in the app.
 * @param route Route.
 * @param segments Segments.
 * @returns Observable boolean to know if continue to navigation.
 */
export const isAuthenticated: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const router = inject(Router);
    const authService = inject(AuthService);
    const loaderService = inject(AppLoaderService);
    const settingsService = inject(SettingsService);
    const appSessionService = inject(AppSessionService);
    const systemUsersService = inject(SystemUsersService);

    if(appSessionService.authorized)
        return of(true);

    loaderService.startLoading();

    return systemUsersService.getLoggedInSystemUser$()
        .pipe(
            switchMap((response) => {
                appSessionService.setSession(response);
                return settingsService.getSettings$()
            }),
            switchMap((response) => {
                settingsService.setting = response;
                settingsService.loadedSettings = true;
                loaderService.stopLoading();
                return of(true);
            }),
            catchError((error: ApiError) => {
                loaderService.stopLoading();

                if (error.status === 403) {
                    console.warn('Have no permissions to access.', error);
                    router.navigate(['/', 'forbidden']);
                    return of(false);
                }

                if (error.status === 401) {
                    appSessionService.removeSession();
                    authService.login();
                    return of(false);
                }

                return of(false);
            })
        );
};

/**
 * Validate if a user is authenticated in the app but has no privilegies to navigate.
 * @param route Route.
 * @param segments Segments.
 * @returns Observable boolean to know if continue to navigation.
 */
export const isForbidden: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const router = inject(Router);
    const authService = inject(AuthService);
    const loaderService = inject(AppLoaderService);
    const appSessionService = inject(AppSessionService);
    const systemUsersService = inject(SystemUsersService);

    if(appSessionService.authorized) {
        router.navigate(['/', 'explorer']);
        return of(false);
    }
    
    loaderService.startLoading();

    return systemUsersService.getLoggedInSystemUser$()
        .pipe(
            switchMap(() => {
                router.navigate(['/', 'explorer']);
                return of(false);
            }),
            catchError((error: ApiError) => {

                if (error.status === 403) {
                    return authService
                        .getSystemUserLoggedInSessionClaims$()
                        .pipe(
                            switchMap((response: Session) => {
                                loaderService.stopLoading();
                                appSessionService.setSession(response, false);
                                return of(true);
                            })
                        );
                }

                loaderService.stopLoading();

                if (error.status === 401) {
                    appSessionService.removeSession();
                    authService.login();
                    return of(false);
                }

                return of(false);
            })
        );
};
